import { map, parseInt, kebabCase, orderBy } from 'lodash';
import PlanUtilities from './PlanService';
import PlanType from '../common/PlanType';
import RestService from './RestService';

const findLowestPrice = (plans) => {
  const orderedPlans = orderBy(plans, 'planPrice', 'asc');
  return orderedPlans[0].planPrice;
};

const mapPlan = (plan) => {
  const planName = plan.planName;
  const id = kebabCase(planName);
  const label = planName
    .replace('Contract - ', '')
    .replace('Skinny ', '');

  let response = {
    planId: plan.planId,
    id,
    label,
    name: planName,
    type: PlanType[plan.planType],
    price: parseInt(plan.planPrice),
    isContract: plan.contract,
    term: plan.term,
    description: PlanUtilities.getDescription(label),
    upload: PlanUtilities.getUploadSpeeds(label),
    download: PlanUtilities.getDownloadSpeeds(label),
    isSpecial: false,
    stopSell: plan.stopSell,
  };
  const oldPrice = parseInt(plan.planOldPrice);
  if (oldPrice) {
    response = { ...response, oldPrice };
  }
  return response;
};

const getPlanData = (entity) => {
  const plans = map(entity.planInfoLst, mapPlan);
  return {
    plans,
    connectionPrice: parseInt(entity.connectionPrice),
    modemPrice: parseInt(entity.modemPrice),
    shippingPrice: parseInt(entity.modemShippingPrice),
    ufbAvailable: entity.ufbAvailable,
    ufbReady: entity.ufbReady,
    isCurrentPlanAvailable: entity.currentPlanAvailable,
    provider: entity.provider,
    byoModemDiscountPrice: parseInt(entity.bYOModemDiscountPrice)
  };
};

/**
 * Return a Promise to call the bestProducts api.
 * @param tui use to check for available plans, takes precedence over samId if present.
 * @param samIds list of samIds to check for available plans on, only used it tui has no value.
 * @returns Promise
 */
const getBestPlans = (tui, samIds) => {
  if (tui) {
    return RestService.get(`/api/v1/product/bestProducts?tui=${tui}`);
  }
  return RestService.get(`/api/v1/product/bestProducts?samIds=${samIds}`);
};

/**
 * Return a Promise to call either the bestProducts or moaAvailableProduct APIs depending on the value of
 * isFetchAllAvailable.
 * Both these APIs return the same type of JSON object containing the results of the call.
 * @param addressId to check product availability on if isFetchAllAvailable is true otherwise the tui.
 * @param isFetchAllAvailable if true the moaAvailableProduct is called otherwise the bestProducts is used.
 * @returns {
 *  Promise<{
 *    planData: {
 *      ufbReady: *,
 *      connectionPrice: number,
 *      shippingPrice: number,
 *      plans: Array,
 *      modemPrice: number,
 *      isCurrentPlanAvailable: *,
 *      ufbAvailable: *,
 *      byoModemDiscountPrice: number
 *    },
 *    status: *}
 *    |
 *    {
 *      message: *,
 *      status: *
 *    }
 *  >}
 */
const fetchBestPlans = async (tui, isFetchAllAvailable, addressId) => {
  try {
    let uri = `/api/v1/product/bestProducts?tui=${tui}`;
    if (isFetchAllAvailable) {
      uri = `/api/v1/account/moaAvailableProduct?tui=${tui}`;
    }
    if (addressId) {
      await RestService.get(uri);
      uri = `/api/v1/product/productAvailability?addressId=${addressId}&planId=${tui}`;
    }
    const response = await RestService.get(uri);
    return { status: response.status.code, planData: getPlanData(response.entity) };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return { status: error.status.code, message: error.status.text };
  }
};

const fetchBestPlanById = async (addressId, planId) => {
  try {
    const uri = `/api/v1/product/productAvailability?addressId=${addressId}&planId=${planId}`;
    const response = await RestService.get(uri);
    return {
      status: response.status.code,
      planData: getPlanData(response.entity),
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return { status: error.status.code, message: error.status.text };
  }
};

export default {
  fetchBestPlans,
  getPlanData,
  getBestPlans,
  findLowestPrice,
  fetchBestPlanById,
};



/** WEBPACK FOOTER **
 ** ./src/services/BestPlansService.js
 **/